import InboxDomain, { teamInboxKey } from "entities/domain/inbox";
import type { ConversationTab } from "redux/features/conversations";

/**
 * Which slice of the merchant's conversations an inbox view is looking at:
 * every Inbox, or one of them. General Enquiries is an ordinary Inbox with its
 * own id.
 *
 * Orthogonal to the ownership lens (ConversationTab = personal | team |
 * unassigned): an Inbox is where a conversation belongs, not who handles it. A
 * conversation owned by an adviser still belongs to an Inbox, so General
 * Enquiries is not "Unassigned". It is a filter, never a permission boundary.
 */
export type InboxScope = { kind: "all" } | { kind: "inbox"; inboxId: string };

export const ALL_INBOXES_SCOPE: InboxScope = { kind: "all" };

const INBOX_SEARCH_PARAM = "inbox";
const TAB_SEARCH_PARAM = "tab";
// Release 1 links and bookmarks. Only ever translated through the Inbox list
// and rewritten to `inbox`, never sent to the API.
//
// Kept by decision (ENG-104, 2 Nov 2026). Delete the legacy branch once
// LogRocket shows no "Legacy Team scope resolved" event for 30 consecutive
// days. The branch is these params, the `team:` / `no_team` saved form below,
// the Release 1 helpers in this file with every caller, and
// LEGACY_TEAM_SCOPE_STORAGE_KEY in redux/features/conversations.
const LEGACY_TEAM_SEARCH_PARAM = "team";
const LEGACY_NO_TEAM_SEARCH_PARAM = "no_team";

const SERIALIZED_ALL = "all";
const SERIALIZED_INBOX_PREFIX = "inbox:";
// Release 1 saved choices. Kept by the same decision as the legacy params
// above, and deleted on the same condition.
const SERIALIZED_LEGACY_TEAM_PREFIX = "team:";
const SERIALIZED_LEGACY_NO_TEAM = "no_team";

// The API cannot count Release 1 scopes: they are translated here, in the
// browser, and never sent. This LogRocket custom event is the only count.
const LEGACY_TEAM_SCOPE_RESOLVED_EVENT = "Legacy Team scope resolved";

export const inboxScopeForInbox = (inboxId: string): InboxScope => ({
  kind: "inbox",
  inboxId,
});

/**
 * Query params for the conversation *list* endpoints and the conversation
 * templates endpoint.
 *
 * `all` sends nothing, so it stays byte-identical to the request the general
 * inbox has always made. An Inbox sends its id and nothing Team-keyed, so the
 * API's mixed-argument 422 is unreachable and the request never counts against
 * the Team-keyed usage log.
 */
export const inboxScopeToListParams = (
  scope: InboxScope
): Record<string, string> =>
  scope.kind === "inbox" ? { inbox_id: scope.inboxId } : {};

/** Stable string form for react-query keys and localStorage. */
export const serializeInboxScope = (scope: InboxScope): string =>
  scope.kind === "inbox"
    ? `${SERIALIZED_INBOX_PREFIX}${scope.inboxId}`
    : SERIALIZED_ALL;

export const deserializeInboxScope = (value: unknown): InboxScope | null => {
  if (typeof value !== "string") {
    return null;
  }

  if (value === SERIALIZED_ALL) {
    return ALL_INBOXES_SCOPE;
  }

  if (
    value.startsWith(SERIALIZED_INBOX_PREFIX) &&
    value.length > SERIALIZED_INBOX_PREFIX.length
  ) {
    return inboxScopeForInbox(value.slice(SERIALIZED_INBOX_PREFIX.length));
  }

  return null;
};

export const inboxScopesEqual = (a: InboxScope, b: InboxScope): boolean =>
  serializeInboxScope(a) === serializeInboxScope(b);

/**
 * A Release 1 scope, still found in old links and saved choices: a Team, or
 * the teamless conversations that are now General Enquiries.
 */
export type LegacyTeamScope =
  | { kind: "team"; teamId: string }
  | { kind: "no_team" };

/** The Release 1 storage form ("team:<uuid>" / "no_team"); "all" is not one. */
export const deserializeLegacyTeamScope = (
  value: unknown
): LegacyTeamScope | null => {
  if (typeof value !== "string") {
    return null;
  }

  if (value === SERIALIZED_LEGACY_NO_TEAM) {
    return { kind: "no_team" };
  }

  if (
    value.startsWith(SERIALIZED_LEGACY_TEAM_PREFIX) &&
    value.length > SERIALIZED_LEGACY_TEAM_PREFIX.length
  ) {
    return {
      kind: "team",
      teamId: value.slice(SERIALIZED_LEGACY_TEAM_PREFIX.length),
    };
  }

  return null;
};

/**
 * The Release 1 scope a URL names, with Release 1's own precedence: `team`
 * wins over `no_team`, and only the literal `no_team=true` counts.
 */
const parseLegacyTeamScopeFromSearch = (
  params: URLSearchParams
): LegacyTeamScope | null => {
  const teamId = params.get(LEGACY_TEAM_SEARCH_PARAM);

  if (teamId) {
    return { kind: "team", teamId };
  }

  return params.get(LEGACY_NO_TEAM_SEARCH_PARAM) === "true"
    ? { kind: "no_team" }
    : null;
};

/**
 * The Inbox a Release 1 scope now means: the Team's canonical Inbox (found by
 * its key, never as the first Inbox linked to the Team, since several may
 * share one), or General Enquiries alone for No team — other Team-less
 * Inboxes are destinations of their own. A Team that has no Inbox here —
 * deleted, or another merchant's — is All.
 */
export const inboxScopeForLegacyTeamScope = (
  legacy: LegacyTeamScope,
  inboxes: InboxDomain[]
): InboxScope => {
  const inbox = inboxes.find((candidate) =>
    legacy.kind === "team"
      ? candidate.key === teamInboxKey(legacy.teamId)
      : candidate.isGeneralEnquiries()
  );

  return inbox ? inboxScopeForInbox(inbox.id) : ALL_INBOXES_SCOPE;
};

/**
 * The Release 1 scope a URL's scope comes from, or `null` when it does not
 * come from one: no legacy params, a malformed `no_team`, or an `inbox` param
 * that wins over them.
 */
export const legacyTeamScopeForSearch = (
  search: string | URLSearchParams
): LegacyTeamScope | null => {
  const params = new URLSearchParams(search);

  return params.get(INBOX_SEARCH_PARAM)
    ? null
    : parseLegacyTeamScopeFromSearch(params);
};

/**
 * Where a resolved Release 1 scope came from: a `team` / `no_team` link, or a
 * Team choice saved on this device.
 */
type LegacyTeamScopeSource = "url" | "saved";

const recordLegacyTeamScopeResolved = async (
  source: LegacyTeamScopeSource,
  legacy: LegacyTeamScope
): Promise<void> => {
  const { default: LogRocket } = await import("logrocket");

  LogRocket.track(LEGACY_TEAM_SCOPE_RESOLVED_EVENT, {
    source,
    kind: legacy.kind,
  });
};

const ignoreTelemetryFailure = (): void => undefined;

/**
 * Counts one resolved Release 1 scope in LogRocket. Call it once per
 * resolution, never per render.
 *
 * Production only, like LogRocket itself, so tests and other builds never
 * load it. Fire and forget: a failed import or track call is ignored, because
 * telemetry must never break navigation.
 */
export const trackLegacyTeamScopeResolved = (
  source: LegacyTeamScopeSource,
  legacy: LegacyTeamScope
): void => {
  if (process.env.REACT_APP_ENVIRONMENT !== "production") {
    return;
  }

  recordLegacyTeamScopeResolved(source, legacy).catch(ignoreTelemetryFailure);
};

/** Whether the URL names a scope at all, in either form. */
export const searchNamesInboxScope = (
  search: string | URLSearchParams
): boolean => {
  const params = new URLSearchParams(search);

  return (
    Boolean(params.get(INBOX_SEARCH_PARAM)) ||
    parseLegacyTeamScopeFromSearch(params) !== null
  );
};

/**
 * The single place a URL becomes an InboxScope — deep links, back/forward and
 * merchant switching all resolve through here.
 *
 * `inbox` is read as it stands. A Release 1 `team` / `no_team` link means
 * nothing until the Inbox list says which Inbox it is now, so it is `null`
 * until `inboxes` is given: there is no honest request to make before then.
 * `inbox` wins over the legacy params when a URL carries both.
 */
export const parseInboxScopeFromSearch = (
  search: string | URLSearchParams,
  inboxes?: InboxDomain[]
): InboxScope | null => {
  const params = new URLSearchParams(search);
  const inboxId = params.get(INBOX_SEARCH_PARAM);

  if (inboxId) {
    return inboxScopeForInbox(inboxId);
  }

  const legacy = parseLegacyTeamScopeFromSearch(params);

  if (!legacy) {
    return ALL_INBOXES_SCOPE;
  }

  return inboxes ? inboxScopeForLegacyTeamScope(legacy, inboxes) : null;
};

/**
 * Whether the URL still carries Release 1 scope params, so it needs rewriting
 * once the Inbox list is known.
 */
export const searchHasLegacyTeamScope = (
  search: string | URLSearchParams
): boolean => {
  const params = new URLSearchParams(search);

  return (
    params.has(LEGACY_TEAM_SEARCH_PARAM) ||
    params.has(LEGACY_NO_TEAM_SEARCH_PARAM)
  );
};

/**
 * A copy of `params` carrying exactly one scope param (or none, for `all`),
 * for feeding back into setSearchParams. Release 1 params are always dropped,
 * so any navigation also finishes canonicalising an old link.
 */
export const applyInboxScopeToSearchParams = (
  params: URLSearchParams,
  scope: InboxScope
): URLSearchParams => {
  const next = new URLSearchParams(params);

  next.delete(INBOX_SEARCH_PARAM);
  next.delete(LEGACY_TEAM_SEARCH_PARAM);
  next.delete(LEGACY_NO_TEAM_SEARCH_PARAM);

  if (scope.kind === "inbox") {
    next.set(INBOX_SEARCH_PARAM, scope.inboxId);
  }

  return next;
};

/**
 * A copy of `params` naming one inbox view: an Inbox scope plus an ownership
 * lens.
 *
 * `tab` is always written, never inherited, because canonicalizeTabSearchParam
 * sets it outside react-router, so the router's copy of the params can lack it.
 */
export const applyInboxViewToSearchParams = (
  params: URLSearchParams,
  scope: InboxScope,
  tab: ConversationTab
): URLSearchParams => {
  const next = applyInboxScopeToSearchParams(params, scope);

  next.set(TAB_SEARCH_PARAM, tab);

  return next;
};

/**
 * Collapses a scope the merchant's Inboxes cannot honour — an archived or
 * retired Inbox, or another merchant's — to All, for this render only; callers
 * must not overwrite a saved preference with the result. Only for restoring a
 * saved choice that does not apply here: a scope the user is looking at stays
 * as it is and is shown as unavailable (isInboxScopeUnavailable), never
 * silently widened to All.
 */
export const resolveInboxScopeAgainstInboxes = (
  scope: InboxScope,
  inboxes: InboxDomain[]
): InboxScope => {
  return isInboxScopeUnavailable(scope, inboxes) ? ALL_INBOXES_SCOPE : scope;
};

/**
 * Whether a scope names an Inbox the merchant's active Inboxes no longer
 * include: archived, retired with its Team, or another merchant's. The list
 * keeps asking for that Inbox (the API matches nothing) rather than for All.
 */
export const isInboxScopeUnavailable = (
  scope: InboxScope,
  inboxes: InboxDomain[]
): boolean =>
  scope.kind === "inbox" &&
  !inboxes.some((inbox) => inbox.id === scope.inboxId);
